/**
 * Keycloak/OIDC utility functions for extracting roles, permissions, and user info
 * from the react-oidc-context auth object.
 */

function decodeAccessTokenPayload(auth) {
  try {
    const token = auth?.user?.access_token;
    if (!token) return {};
    const parts = token.split('.');
    if (parts.length !== 3) return {};
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload;
  } catch {
    return {};
  }
}

export function hasRole(auth, roleName) {
  return getRoles(auth).includes(roleName);
}

export function getRoles(auth) {
  // realm_access is in the access token, not always in the ID token
  const fromProfile = auth?.user?.profile?.realm_access?.roles;
  if (fromProfile && fromProfile.length > 0) return fromProfile;
  const payload = decodeAccessTokenPayload(auth);
  return payload?.realm_access?.roles || [];
}

export function extractPermissions(auth) {
  const roles = getRoles(auth);

  let groupLevel = -1;
  for (const r of roles) {
    if (r.startsWith('group_level_')) {
      const level = parseInt(r.split('_').pop(), 10);
      if (!isNaN(level) && level > groupLevel) groupLevel = level;
    }
  }
  const hasOnlyMTRLevel = roles.includes('group_level_0') && groupLevel === 0;

  return {
    isAdmin: roles.includes('admin'),
    canManageReporting: roles.includes('can_manage_reporting'),
    canApprove: roles.includes('can_approve'),
    canVerify: roles.includes('can_verify'),
    canViewCalendar: roles.includes('can_view_calendar'),
    canEditCalendar: roles.includes('can_edit_calendar'),
    canViewPersonalStats: roles.includes('can_view_personal_stats'),
    canViewTeamStats: roles.includes('can_view_team_stats'),
    canViewModalityStats: roles.includes('can_view_modality_stats'),
    canViewStudyStats: roles.includes('can_view_query_builder'),
    canDownloadPersonalStats: roles.includes('can_download_personal_stats'),
    canDownloadTeamStats: roles.includes('can_download_team_stats'),
    canDownloadModalityStats: roles.includes('can_download_modality_stats'),
    canDownloadStudyStats: roles.includes('can_download_query_builder'),
    canViewStats:
      roles.includes('admin') ||
      roles.includes('can_view_personal_stats') ||
      roles.includes('can_view_team_stats') ||
      roles.includes('can_view_modality_stats') ||
      roles.includes('can_view_query_builder') ||
      roles.includes('can_download_personal_stats') ||
      roles.includes('can_download_team_stats') ||
      roles.includes('can_download_modality_stats') ||
      roles.includes('can_download_query_builder'),
    usesDarkmode: false, // loaded from backend /auth/me
    isMTR: hasOnlyMTRLevel,
  };
}

export function getUserInfo(auth) {
  const profile = auth?.user?.profile || {};
  return {
    sub: profile.sub,
    username: profile.preferred_username,
    email: profile.email,
    firstName: profile.given_name || '',
    lastName: profile.family_name || '',
    fullName: [profile.given_name, profile.family_name].filter(Boolean).join(' ') || profile.preferred_username,
    academicDegree: profile.academic_degree || '',
    position: profile.position || '',
    jobTitle: profile.job_title || '',
    tenantIds: profile.tenant_ids || '1',
  };
}

export function getAccessToken(auth) {
  return auth?.user?.access_token || null;
}
